/**
 * System Prompt Builder for Marta AI Assistant
 *
 * Generates dynamic system prompts that include:
 * - Marta's personality and behavior guidelines
 * - Available tools schema in JSON format
 * - Instructions for structured tool calling (ReAct pattern)
 * - Rules for confirmations on destructive operations
 */

import { getToolsSchema, toolRegistry, registerTools } from '../tools/registry';
import type { ToolSchema, ToolCategory, ToolDefinition } from '../tools/types';
import { projectTools } from '../tools/projectTools';
import { calendarTools } from '../tools/calendarTools';
import { contactTools } from '../tools/contactTools';
import { accountingTools } from '../tools/accountingTools';
import { compileContext, buildSystemPromptFromContext } from '../memory';
import type { AICompiledContext } from '../memory';

/**
 * Initialize all tools in the registry.
 * Called automatically when building system prompt if registry is empty.
 */
function initializeTools(): void {
  registerTools(projectTools);
  registerTools(calendarTools);
  registerTools(contactTools);
  registerTools(accountingTools);
}

/**
 * Get all tool definitions for category grouping
 */
function getAllToolDefinitions(): ToolDefinition[] {
  return [
    ...projectTools,
    ...calendarTools,
    ...contactTools,
    ...accountingTools,
  ];
}

// =============================================================================
// PERSONA & BASE PROMPT
// =============================================================================

const MARTA_PERSONA = `Sei Marta, un'assistente business intelligente e proattiva.

## PERSONALITÀ
- Professionale ma amichevole, con un tocco di calore umano
- Efficiente e orientata ai risultati
- Chiara e concisa nelle risposte
- Proattiva nel suggerire azioni utili
- Rispettosa del tempo dell'utente

## COMPORTAMENTO
- Rispondi sempre in italiano
- Sii diretta e vai al punto
- Quando esegui azioni, conferma sempre cosa hai fatto
- Se qualcosa non è chiaro, chiedi chiarimenti
- Suggerisci azioni correlate quando appropriato`;

// =============================================================================
// TOOLS INSTRUCTIONS
// =============================================================================

const TOOLS_INSTRUCTIONS = `
## IMPORTANTE: COME ACCEDERE AI DATI

NON INVENTARE MAI dati come progetti, task, contatti, ecc.
DEVI SEMPRE usare i tools per accedere ai dati reali nel database.

### FORMATO TOOL CALL

Quando devi cercare, creare, modificare o eliminare dati, rispondi SOLO con questo JSON:

{"thought": "ragionamento", "tool": "nome_tool", "parameters": {...}}

### ESEMPI CORRETTI:

Utente: "Quali progetti ho?"
Risposta: {"thought": "Devo cercare i progetti nel database", "tool": "search_projects", "parameters": {}}

Utente: "Mostrami i progetti attivi"
Risposta: {"thought": "Cerco progetti con status active", "tool": "search_projects", "parameters": {"status": "active"}}

Utente: "Crea un task chiamato Test"
Risposta: {"thought": "Creo il task", "tool": "create_task", "parameters": {"title": "Test", "projectId": "..."}}

### ERRORE COMUNE DA EVITARE:
MAI rispondere con dati inventati tipo {"projects": [...]}
SEMPRE usare {"tool": "search_projects", "parameters": {...}}

### REGOLE:
1. Per QUALSIASI domanda su dati (progetti, task, eventi, contatti), USA IL TOOL appropriato
2. NON inventare mai dati - usa sempre i tools
3. Dopo il tool, riceverai i risultati reali e potrai rispondere
4. Per eliminazioni, chiedi conferma prima`;

// =============================================================================
// TOOL SCHEMA FORMATTING
// =============================================================================

/**
 * Format tools schema for inclusion in system prompt
 */
function formatToolsSchema(tools: ToolSchema[]): string {
  return tools.map(tool => {
    const params = tool.parameters.properties || {};
    const required = tool.parameters.required || [];

    const paramsDescription = Object.entries(params)
      .map(([name, schema]: [string, { description?: string; type?: string; enum?: string[] }]) => {
        const isRequired = required.includes(name);
        const typeStr = schema.enum ? `enum[${schema.enum.join('|')}]` : schema.type || 'any';
        return `    - ${name}${isRequired ? '*' : ''}: ${typeStr} - ${schema.description || ''}`;
      })
      .join('\n');

    const confirmNote = tool.requiresConfirmation ? ' [RICHIEDE CONFERMA]' : '';

    return `### ${tool.name}${confirmNote}
${tool.description}
Parametri:
${paramsDescription || '    (nessun parametro)'}`;
  }).join('\n\n');
}

/**
 * Group tools by category for better readability
 */
function groupToolsByCategory(tools: ToolSchema[]): Record<string, ToolSchema[]> {
  const toolDefinitions = getAllToolDefinitions();
  const groups: Record<string, ToolSchema[]> = {};

  tools.forEach(tool => {
    const def = toolDefinitions.find(d => d.name === tool.name);
    const category = def?.category || 'other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(tool);
  });

  return groups;
}

/**
 * Get category display name in Italian
 */
function getCategoryDisplayName(category: string): string {
  const names: Record<string, string> = {
    projects: 'Progetti',
    tasks: 'Task',
    calendar: 'Calendario',
    contacts: 'Contatti (CRM)',
    accounting: 'Contabilità',
    other: 'Altri',
  };
  return names[category] || category;
}

// =============================================================================
// PROMPT BUILDERS
// =============================================================================

export interface SystemPromptOptions {
  /** Include all tools (default: true) */
  includeAllTools?: boolean;
  /** Specific categories to include */
  categories?: ToolCategory[];
  /** Include current date/time context */
  includeDateTime?: boolean;
  /** Additional context to append */
  additionalContext?: string;
  /** User name for personalization */
  userName?: string;
  /** Compact mode (shorter prompt) */
  compact?: boolean;
  /** User ID for 4-layer memory context */
  userId?: string;
  /** Pre-compiled memory context (to avoid re-fetching) */
  memoryContext?: AICompiledContext;
}

/**
 * Build the complete system prompt for Marta
 */
export function buildSystemPrompt(options: SystemPromptOptions = {}): string {
  const {
    includeAllTools = true,
    categories,
    includeDateTime = true,
    additionalContext,
    userName,
    compact = false,
  } = options;

  // Ensure tools are initialized
  if (toolRegistry.size === 0) {
    initializeTools();
  }

  // Get tools schema
  let toolsSchema: ToolSchema[];
  if (categories && categories.length > 0) {
    toolsSchema = toolRegistry.getToolsSchemaByCategories(categories);
  } else if (includeAllTools) {
    toolsSchema = getToolsSchema();
  } else {
    toolsSchema = [];
  }

  // Build sections
  const sections: string[] = [];

  // 1. Persona
  sections.push(MARTA_PERSONA);

  // 2. Greeting with user name
  if (userName) {
    sections.push(`\nStai assistendo ${userName}.`);
  }

  // 3. Date/time context
  if (includeDateTime) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const timeStr = now.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });
    sections.push(`\n## CONTESTO TEMPORALE\nOggi è ${dateStr}, ore ${timeStr}.`);
  }

  // 4. Tools
  if (toolsSchema.length > 0) {
    sections.push(TOOLS_INSTRUCTIONS);

    if (compact) {
      // Compact mode: just list tool names
      const toolNames = toolsSchema.map(t => t.name).join(', ');
      sections.push(`\n## TOOLS DISPONIBILI\n${toolNames}`);
    } else {
      // Full mode: detailed schema by category
      const grouped = groupToolsByCategory(toolsSchema);
      let toolsSection = '\n## TOOLS DISPONIBILI\n';

      for (const [category, tools] of Object.entries(grouped)) {
        toolsSection += `\n### ${getCategoryDisplayName(category)}\n`;
        toolsSection += formatToolsSchema(tools);
        toolsSection += '\n';
      }

      sections.push(toolsSection);
    }
  }

  // 5. Additional context
  if (additionalContext) {
    sections.push(`\n## CONTESTO AGGIUNTIVO\n${additionalContext}`);
  }

  return sections.join('\n');
}

/**
 * Build a minimal system prompt without tools (for simple conversations)
 */
export function buildMinimalPrompt(options: Omit<SystemPromptOptions, 'includeAllTools' | 'categories'> = {}): string {
  return buildSystemPrompt({
    ...options,
    includeAllTools: false,
  });
}

/**
 * Build a system prompt for a specific domain
 */
export function buildDomainPrompt(
  domain: ToolCategory | ToolCategory[],
  options: Omit<SystemPromptOptions, 'categories'> = {}
): string {
  const categories = Array.isArray(domain) ? domain : [domain];
  return buildSystemPrompt({
    ...options,
    categories,
  });
}

/**
 * Build a system prompt for projects and tasks only
 */
export function buildProjectsPrompt(options: SystemPromptOptions = {}): string {
  return buildDomainPrompt(['projects', 'tasks'], options);
}

/**
 * Build a system prompt for calendar only
 */
export function buildCalendarPrompt(options: SystemPromptOptions = {}): string {
  return buildDomainPrompt('calendar', options);
}

/**
 * Build a system prompt for contacts/CRM only
 */
export function buildContactsPrompt(options: SystemPromptOptions = {}): string {
  return buildDomainPrompt('contacts', options);
}

/**
 * Build a system prompt for accounting only
 */
export function buildAccountingPrompt(options: SystemPromptOptions = {}): string {
  return buildDomainPrompt('accounting', options);
}

// =============================================================================
// WEB SEARCH ENHANCED PROMPT
// =============================================================================

export interface SearchResult {
  title: string;
  snippet: string;
  url: string;
}

/**
 * Build a system prompt enhanced with web search results (RAG pattern)
 */
export function buildSearchEnhancedPrompt(
  searchResults: SearchResult[],
  baseOptions: SystemPromptOptions = {}
): string {
  const basePrompt = buildSystemPrompt(baseOptions);

  if (!searchResults || searchResults.length === 0) {
    return basePrompt;
  }

  const resultsSection = searchResults
    .map((r, i) => `${i + 1}. **${r.title}**\n   ${r.snippet}\n   Fonte: ${r.url}`)
    .join('\n\n');

  const searchInstructions = `
## RISULTATI RICERCA WEB

Ho trovato queste informazioni dalla ricerca web:

${resultsSection}

### ISTRUZIONI PER LA RISPOSTA
- Usa queste informazioni per rispondere alla domanda dell'utente
- Cita sempre le fonti quando usi informazioni specifiche
- Se le informazioni sono contraddittorie, segnalalo
- Se non trovi informazioni rilevanti, dillo chiaramente`;

  return `${basePrompt}\n${searchInstructions}`;
}

// =============================================================================
// TOOL RESULT INJECTION
// =============================================================================

/**
 * Create a message to inject tool results back to the LLM
 */
export function createToolResultInjection(
  toolName: string,
  success: boolean,
  result: unknown
): string {
  if (success) {
    return `[RISULTATO TOOL: ${toolName}]
Operazione completata con successo.

Dati:
\`\`\`json
${JSON.stringify(result, null, 2)}
\`\`\`

Ora rispondi all'utente in italiano, riassumendo il risultato in modo chiaro e conciso.`;
  } else {
    return `[ERRORE TOOL: ${toolName}]
L'operazione non è riuscita.

Errore: ${result}

Spiega all'utente cosa è successo e suggerisci come procedere.`;
  }
}

/**
 * Create a confirmation request message
 */
export function createConfirmationRequest(
  entityType: string,
  entityName: string,
  action: string = 'eliminare'
): string {
  return `Vuoi ${action} ${entityType} "${entityName}"?

⚠️ **Questa azione è irreversibile.**

Scrivi "Sì, elimina" per confermare, oppure "Annulla" per annullare l'operazione.`;
}

/**
 * Create a message for when confirmation is received
 */
export function createConfirmationReceived(entityType: string, entityName: string): string {
  return `Ho eliminato ${entityType} "${entityName}".`;
}

/**
 * Create a message for when action is cancelled
 */
export function createCancellationMessage(entityType: string): string {
  return `Ok, l'eliminazione di ${entityType} è stata annullata. Nessuna modifica è stata apportata.`;
}

// =============================================================================
// 4-LAYER MEMORY INTEGRATION
// =============================================================================

/**
 * Build a complete system prompt with 4-layer memory context.
 * This is the recommended way to build prompts for personalized AI interactions.
 *
 * Layers included:
 * 1. Base Instructions (admin-controlled, universal)
 * 2. Sector Instructions (business-type specific)
 * 3. Collective Patterns (crowdsourced knowledge)
 * 4. User Preferences (personal customizations)
 *
 * @param userId - The user ID to compile context for
 * @param options - Additional prompt options
 */
export async function buildSystemPromptWithMemory(
  userId: string,
  options: Omit<SystemPromptOptions, 'userId'> = {}
): Promise<string> {
  // Compile 4-layer memory context
  const memoryContext = await compileContext(userId);

  // Build memory section from context
  const memorySection = buildSystemPromptFromContext(memoryContext);

  // Build tools section
  const toolsPrompt = buildSystemPrompt({
    ...options,
    // Don't include default persona - we're using memory layers
  });

  // Remove the default MARTA_PERSONA from toolsPrompt since we have memory layers
  const toolsOnlyPart = toolsPrompt.replace(/^Sei Marta[\s\S]*?## CONTESTO TEMPORALE/m, '## CONTESTO TEMPORALE');

  // Combine: Memory context (all 4 layers) + Tools
  return `${memorySection}\n\n${toolsOnlyPart}`;
}

/**
 * Get compiled memory context for a user (useful for caching).
 */
export async function getMemoryContext(userId: string): Promise<AICompiledContext> {
  return compileContext(userId);
}

/**
 * Build system prompt using pre-compiled memory context.
 * Useful when you want to cache the memory context across multiple calls.
 */
export function buildSystemPromptFromMemoryContext(
  memoryContext: AICompiledContext,
  options: Omit<SystemPromptOptions, 'userId' | 'memoryContext'> = {}
): string {
  // Build memory section from context
  const memorySection = buildSystemPromptFromContext(memoryContext);

  // Build tools section
  const toolsPrompt = buildSystemPrompt(options);

  // Remove the default MARTA_PERSONA from toolsPrompt
  const toolsOnlyPart = toolsPrompt.replace(/^Sei Marta[\s\S]*?## CONTESTO TEMPORALE/m, '## CONTESTO TEMPORALE');

  return `${memorySection}\n\n${toolsOnlyPart}`;
}
